/**
 * Registry for uploaded photo URLs that need to be included in mutations
 *
 * When files are uploaded offline and then synced, their real Azure URLs need
 * to be included in the corresponding entity create/patch mutations.
 *
 * This registry stores the mapping from entityId to uploaded URLs, so that
 * the syncManager can include them in mutations before sending.
 */

type EntityPhotoRegistry = Map<string, string[]>;

// Map of entityId to array of uploaded photo URLs
const uploadedPhotoUrls: EntityPhotoRegistry = new Map();

const localToRemoteUrlMap: Map<string, string> = new Map();

/**
 * Create a key for the registry (entityType:entityId)
 */
function getKey(entityType: string, entityId: string): string {
  return `${entityType}:${entityId}`;
}

/**
 * Add an uploaded photo URL for an entity
 * Called by fileSyncManager after successful upload
 */
export function addUploadedPhotoUrl(entityType: string, entityId: string, photoUrl: string): void {
  const key = getKey(entityType, entityId);
  const existing = uploadedPhotoUrls.get(key) || [];
  uploadedPhotoUrls.set(key, [...existing, photoUrl]);
}

export function addLocalToRemoteUrlMapping(localUrl: string, remoteUrl: string): void {
  localToRemoteUrlMap.set(localUrl, remoteUrl);
}

export function flushLocalToRemoteUrlMappings(): void {
  localToRemoteUrlMap.clear();
}

export function getRemoteUrlMapping(localUrl: string): string | undefined {
  return localToRemoteUrlMap.get(localUrl);
}

/**
 * Get all uploaded photo URLs for an entity
 * Called by syncManager before sending mutations
 */
export function getUploadedPhotoUrls(entityType: string, entityId: string): string[] {
  const key = getKey(entityType, entityId);
  return uploadedPhotoUrls.get(key) || [];
}

/**
 * Clear uploaded photo URLs for an entity (after mutation is sent)
 */
export function clearUploadedPhotoUrls(entityType: string, entityId: string): void {
  const key = getKey(entityType, entityId);
  uploadedPhotoUrls.delete(key);
}

/**
 * Check if there are any pending photo URLs for an entity
 */
export function hasUploadedPhotoUrls(entityType: string, entityId: string): boolean {
  const key = getKey(entityType, entityId);
  const urls = uploadedPhotoUrls.get(key);
  return !!urls && urls.length > 0;
}

/**
 * Get all entities that have pending photo URLs
 * Returns array of { entityType, entityId, urls }
 */
export function getAllPendingPhotos(): Array<{ entityType: string; entityId: string; urls: string[] }> {
  const result: Array<{ entityType: string; entityId: string; urls: string[] }> = [];
  uploadedPhotoUrls.forEach((urls, key) => {
    const [entityType, entityId] = key.split(':');
    result.push({ entityType, entityId, urls });
  });
  return result;
}

/**
 * Clear all pending photo URLs
 */
export function clearAllUploadedPhotoUrls(): void {
  uploadedPhotoUrls.clear();
}
